import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import {
  Badge,
  Banner,
  Card,
  Container,
  HStack,
  Heading,
  Link,
  Spinner,
  Text,
  VStack,
} from 'wss3-forge'
import { ArrowLeft16Regular } from '@fluentui/react-icons'
import {
  getSessionMessages,
  listSessions,
  type SessionMessage,
  type SessionSummary,
} from '../lib/api.js'

const CHANNEL_LABEL: Record<string, string> = {
  pwa: 'Chat',
  voice: 'Voix',
  telegram: 'Telegram',
  system: 'Système',
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  return sameDay
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function truncate(s: string | null, max = 140): string {
  if (!s) return ''
  if (s.length <= max) return s
  return s.slice(0, max - 1).trimEnd() + '…'
}

function SessionList() {
  const navigate = useNavigate()
  const sessionsQuery = useQuery<SessionSummary[]>({
    queryKey: ['sessions'],
    queryFn: () => listSessions(50),
  })

  const sessions = sessionsQuery.data ?? []

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Sessions</Heading>
          <Text color="muted">Tes conversations passées.</Text>
        </VStack>

        {sessionsQuery.error && (
          <Banner variant="error" title="Échec du chargement">
            {String((sessionsQuery.error as Error).message)}
          </Banner>
        )}

        {sessionsQuery.isLoading && (
          <HStack gap="sm" align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Chargement…</Text>
          </HStack>
        )}

        {!sessionsQuery.isLoading && sessions.length === 0 && (
          <Banner variant="info">Aucune conversation enregistrée pour l'instant.</Banner>
        )}

        <VStack gap="sm" fullWidth>
          {sessions.map((s) => (
            <Card
              key={s.session_id}
              padding="md"
              hoverable
              onClick={() => navigate(`/sessions/${s.session_id}`)}
            >
              <VStack gap="xs" fullWidth>
                <HStack justify="between" align="center" gap="sm" fullWidth>
                  <HStack gap="xs" align="center">
                    <Badge variant="default">
                      {CHANNEL_LABEL[s.last_channel] ?? s.last_channel}
                    </Badge>
                    <Text size="xs" color="muted">
                      {s.message_count} message(s) · {formatDate(s.last_at)}
                    </Text>
                  </HStack>
                  <Text size="xs" color="muted">{s.session_id.slice(0, 8)}</Text>
                </HStack>
                {s.title && (
                  <Text size="md" weight="semibold">
                    {s.title}
                  </Text>
                )}
                {s.first_user_message && (
                  <Text size="sm" color="muted">
                    {truncate(s.first_user_message, 160)}
                  </Text>
                )}
              </VStack>
            </Card>
          ))}
        </VStack>
      </VStack>
    </Container>
  )
}

function SessionDetail({ id }: { id: string }) {
  const navigate = useNavigate()
  const messagesQuery = useQuery<SessionMessage[]>({
    queryKey: ['session-messages', id],
    queryFn: () => getSessionMessages(id),
  })

  const messages = messagesQuery.data ?? []

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <HStack gap="xs" align="center">
          <ArrowLeft16Regular style={{ color: 'var(--text-muted)' }} />
          <Link variant="muted" size="sm" onClick={() => navigate('/sessions')}>
            Toutes les sessions
          </Link>
        </HStack>

        <VStack gap="sm">
          <Heading level={3}>Session {id.slice(0, 8)}</Heading>
          <Text color="muted">{messages.length} message(s)</Text>
        </VStack>

        {messagesQuery.error && (
          <Banner variant="error" title="Échec du chargement">
            {String((messagesQuery.error as Error).message)}
          </Banner>
        )}

        {messagesQuery.isLoading && (
          <HStack gap="sm" align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Chargement…</Text>
          </HStack>
        )}

        <VStack gap="md" fullWidth>
          {messages.map((m) => {
            const isUser = m.role === 'user'
            // User vs assistant differentiated by Forge variant alone (outlined
            // reads as a "you" bubble, subtle as a passive transcript line).
            // No brand tint behind the assistant — colored surfaces are
            // reserved for status, not authorship.
            return (
              <Card
                key={m.id}
                padding="md"
                variant={isUser ? 'outlined' : 'subtle'}
              >
                <VStack gap="xs" fullWidth>
                  <HStack gap="xs" align="center">
                    <Badge variant="default">
                      {m.role === 'user' ? 'Toi' : m.role === 'assistant' ? 'Stark' : m.role}
                    </Badge>
                    <Text size="xs" color="muted">
                      {CHANNEL_LABEL[m.channel] ?? m.channel} · {formatDate(m.created_at)}
                    </Text>
                  </HStack>
                  <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{m.content}</Text>
                </VStack>
              </Card>
            )
          })}
        </VStack>
      </VStack>
    </Container>
  )
}

export function SessionsPage() {
  const { id } = useParams<{ id: string }>()
  return id ? <SessionDetail id={id} /> : <SessionList />
}
